import { ReactElement } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import { LegalBrand, LegalPageLayout, List, Section } from "./LegalPageLayout";

const CONTACT = "hello@delgentic.com";

/** Same Google policy the privacy notice links. The calendar section repeats the
 * Limited Use statement because a reviewer reads the terms and the policy separately. */
const GOOGLE_USER_DATA_POLICY =
  "https://developers.google.com/terms/api-services-user-data-policy";

/** Danish consumer complaints board. Required signposting for a Danish trader
 * selling to consumers; the EU ODR platform it used to sit beside closed in 2025. */
const COMPLAINTS_BOARD = "https://naevneneshus.dk";

/**
 * A section that is a heading plus one paragraph.
 */
function Prose({
  heading,
  body,
  components,
}: {
  heading: string;
  body: string;
  components: Record<string, ReactElement>;
}) {
  return (
    <Section heading={heading}>
      <p>
        <Trans i18nKey={body} components={components} />
      </p>
    </Section>
  );
}

/**
 * A section that is a heading, an optional lead-in paragraph, and a bulleted list.
 * {@code count} is passed rather than derived because the copy lives in the i18n
 * bundle and {@code t()} cannot report the length of an array behind a key.
 */
function Bullets({
  heading,
  base,
  count,
  intro,
  components,
}: {
  heading: string;
  base: string;
  count: number;
  intro?: string;
  components: Record<string, ReactElement>;
}) {
  return (
    <Section heading={heading}>
      {intro ? (
        <p>
          <Trans i18nKey={intro} components={components} />
        </p>
      ) : null}
      <List>
        {Array.from({ length: count }, (_, i) => (
          <li key={i}>
            <Trans i18nKey={`${base}.${i}`} components={components} />
          </li>
        ))}
      </List>
    </Section>
  );
}

/**
 * Customer Terms of Service, served at {@code /terms} on the apex (delgentic.com) and
 * at the same path in the app bundle. Covers the Free and Basic plans only.
 *
 * <p><b>Deliberately does not mention agent-to-agent scheduling, agent cards,
 * connections or AI drafting.</b> Every one of those sits behind the
 * {@code AI_SCHEDULING} capability, which is Pro and above, so a Free or Basic
 * customer cannot reach any of it. They get their own version of this page when Pro
 * ships, as a version bump of this document rather than a second one.</p>
 *
 * <p>Two clauses here have no equivalent in Calendly's, Luma's or SimplyMeet's terms
 * and exist because of how Scheda actually behaves. {@code terms.availability} says
 * that a free slot is a guide rather than a promise, because the busy-time check can
 * fail open and a revoked calendar permission is invisible to us until the next call.
 * {@code terms.handle} says that changing a handle breaks links already shared,
 * because rolling links resolve by owner handle with no slug fallback. Both are there
 * to stop a real product behaviour reading as a defect. Do not trim them.</p>
 *
 * <p>English only, by decision: a machine translation of binding legal text is a
 * liability, so {@code terms.*} lives in {@code en.json} and nowhere else. With
 * {@code fallbackLng: "en"} a Danish visitor sees the English text, which is what
 * {@code terms.misc} promises. Add {@code da.json} keys only once a lawyer has
 * checked the translation.</p>
 *
 * <h2>Why {@code terms.liability} has a fixed DKK 1,000 floor</h2>
 * The cap is the greater of fees actually paid in the preceding 12 months and DKK 1,000.
 * The floor is the part that looks arbitrary and is not, so the evidence lives here:
 * <ul>
 *   <li><b>SimplyMeet</b> caps at "the amount payable by You" and <b>Luma</b> at "the
 *       actual total amount received by us from you". Both are pure formulas with no
 *       floor, so for a customer on a free plan they evaluate to ZERO.</li>
 *   <li><b>Calendly</b> caps at "the greater of (i) the Fees paid or payable... in the
 *       twelve (12) month period preceding... or (ii) $100 USD" — the same two-part
 *       shape used here, arrived at independently.</li>
 * </ul>
 * A cap of zero against a consumer is close to a total exclusion, which is the classic
 * target under aftaleloven §36 and the EU unfair-terms rules, and a struck cap does not
 * fall back to a smaller one: it leaves no cap at all. So on THIS clause the US-drafted
 * Calendly is the better model, even though the document otherwise follows SimplyMeet as
 * the EU-drafted one. DKK 1,000 (about $145) sits deliberately above Calendly's $100
 * (about DKK 700), because the floor is doing its job under EU consumer law rather than
 * US law. Lowering it, or dropping it for a pure fees formula, would put the whole clause
 * at risk rather than making it stronger.
 *
 * <p>Nothing here may call {@code useSession()} or {@code useAuth()}; see
 * {@code catalog/CatalogApp.tsx} for why the apex bundle cannot survive it.</p>
 */
export function TermsPage({
  brand,
  onSignup,
}: {
  brand: LegalBrand;
  onSignup: () => void;
}) {
  const { t } = useTranslation();

  const rich = {
    strong: <strong />,
    mail: <a className="underline" href={`mailto:${CONTACT}`} />,
    policy: (
      <a
        className="underline"
        href={GOOGLE_USER_DATA_POLICY}
        target="_blank"
        rel="noreferrer"
      />
    ),
    naevn: (
      <a
        className="underline"
        href={COMPLAINTS_BOARD}
        target="_blank"
        rel="noreferrer"
      />
    ),
    // Relative, so each bundle links to its own copy of these pages: the apex
    // serves them under Delgentic's mark, the app under Scheda's.
    privacy: <Link className="underline" to="/privacy" />,
    cookies: <Link className="underline" to="/cookies" />,
  };

  return (
    <LegalPageLayout
      title={t("terms.title")}
      updated={t("terms.updated")}
      brand={brand}
      onSignup={onSignup}
    >
      <p>
        <Trans i18nKey="terms.intro" components={rich} />
      </p>

      <Prose
        heading={t("terms.who.heading")}
        body="terms.who.body"
        components={rich}
      />
      <Prose
        heading={t("terms.service.heading")}
        body="terms.service.body"
        components={rich}
      />
      <Prose
        heading={t("terms.notParty.heading")}
        body="terms.notParty.body"
        components={rich}
      />

      <Bullets
        heading={t("terms.account.heading")}
        base="terms.account.items"
        count={6}
        components={rich}
      />
      <Bullets
        heading={t("terms.handle.heading")}
        base="terms.handle.items"
        count={4}
        intro="terms.handle.intro"
        components={rich}
      />

      <Section heading={t("terms.calendar.heading")}>
        <p>
          <Trans i18nKey="terms.calendar.body" components={rich} />
        </p>
        <List>
          {[0, 1, 2].map((i) => (
            <li key={i}>
              <Trans i18nKey={`terms.calendar.items.${i}`} components={rich} />
            </li>
          ))}
        </List>
      </Section>

      <Prose
        heading={t("terms.availability.heading")}
        body="terms.availability.body"
        components={rich}
      />

      <Section heading={t("terms.email.heading")}>
        <p>
          <Trans i18nKey="terms.email.body" components={rich} />
        </p>
        <List>
          {[0, 1, 2, 3].map((i) => (
            <li key={i}>
              <Trans i18nKey={`terms.email.items.${i}`} components={rich} />
            </li>
          ))}
        </List>
      </Section>

      <Section heading={t("terms.content.heading")}>
        <p>
          <Trans i18nKey="terms.content.body" components={rich} />
        </p>
        <List>
          {[0, 1, 2].map((i) => (
            <li key={i}>
              <Trans i18nKey={`terms.content.items.${i}`} components={rich} />
            </li>
          ))}
        </List>
      </Section>

      <Bullets
        heading={t("terms.conduct.heading")}
        base="terms.conduct.items"
        count={7}
        intro="terms.conduct.intro"
        components={rich}
      />

      <Bullets
        heading={t("terms.limits.heading")}
        base="terms.limits.items"
        count={4}
        intro="terms.limits.intro"
        components={rich}
      />

      <Bullets
        heading={t("terms.payment.heading")}
        base="terms.payment.items"
        count={6}
        intro="terms.payment.intro"
        components={rich}
      />
      <Bullets
        heading={t("terms.renewal.heading")}
        base="terms.renewal.items"
        count={3}
        components={rich}
      />

      <Prose
        heading={t("terms.withdrawal.heading")}
        body="terms.withdrawal.body"
        components={rich}
      />

      <Bullets
        heading={t("terms.downgrade.heading")}
        base="terms.downgrade.items"
        count={4}
        intro="terms.downgrade.intro"
        components={rich}
      />
      {/* Deliberately before suspension: the two enforcement powers read in order of
          severity, so blocking one link is visibly the lesser step. */}
      <Section heading={t("terms.linkAbuse.heading")}>
        <p>
          <Trans i18nKey="terms.linkAbuse.body" components={rich} />
        </p>
        <List>
          {[0, 1, 2, 3].map((i) => (
            <li key={i}>
              <Trans i18nKey={`terms.linkAbuse.items.${i}`} components={rich} />
            </li>
          ))}
        </List>
      </Section>

      <Bullets
        heading={t("terms.suspension.heading")}
        base="terms.suspension.items"
        count={3}
        components={rich}
      />

      <Prose
        heading={t("terms.retention.heading")}
        body="terms.retention.body"
        components={rich}
      />
      <Prose
        heading={t("terms.privacy.heading")}
        body="terms.privacy.body"
        components={rich}
      />

      <Bullets
        heading={t("terms.ownership.heading")}
        base="terms.ownership.items"
        count={4}
        components={rich}
      />

      <Prose
        heading={t("terms.warranty.heading")}
        body="terms.warranty.body"
        components={rich}
      />

      <Bullets
        heading={t("terms.liability.heading")}
        base="terms.liability.items"
        count={3}
        intro="terms.liability.intro"
        components={rich}
      />

      <Prose
        heading={t("terms.indemnity.heading")}
        body="terms.indemnity.body"
        components={rich}
      />
      <Prose
        heading={t("terms.changes.heading")}
        body="terms.changes.body"
        components={rich}
      />

      <Bullets
        heading={t("terms.law.heading")}
        base="terms.law.items"
        count={3}
        components={rich}
      />
      <Bullets
        heading={t("terms.misc.heading")}
        base="terms.misc.items"
        count={8}
        components={rich}
      />

      <Prose
        heading={t("terms.contact.heading")}
        body="terms.contact.body"
        components={rich}
      />
    </LegalPageLayout>
  );
}
